"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import StockHistoryDialog from "@/components/custom/inventory/StockHistoryDialog";
import {
  MagnifyingGlass,
  FunnelSimple,
  PencilSimple,
  Trash,
  ArrowUp,
  ArrowDown,
  Plus,
  ClockCounterClockwise,
  DownloadSimple,
  UploadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  getStocks,
  addStock,
  updateStock,
  deleteStock,
} from "@/state/stock/stock.slice";
import { getProducts } from "@/state/products/product.slice";
import { getSuppliers } from "@/state/suppliers/supplier.slice";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Loader2, Package } from "lucide-react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";

const API_URL = import.meta.env.VITE_API_URL;

// StockForm component defined outside to prevent re-creation on each render
// This fixes the input focus loss issue
const StockForm = ({ stock, setStock, isEdit = false, products, suppliersList }) => (
  <div className="grid gap-4 py-4">
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="product" className="text-right">
        Product
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="col-span-3 justify-between"
          >
            {stock?.product && products
              ? products.find((product) => product.id === stock.product)
                ?.title || "Select product..."
              : "Select product..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="Search product..." />
            <CommandEmpty>No product found.</CommandEmpty>
            <CommandGroup>
              {products && products.length > 0 ? (
                products?.map((product) => (
                  <CommandItem
                    key={product?.id}
                    onSelect={() => {
                      setStock({ ...(stock ?? {}), product: product?.id });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        stock?.product === product?.id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {product?.title}
                  </CommandItem>
                ))
              ) : (
                <CommandItem>Loading products...</CommandItem>
              )}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="quantity" className="text-right">
        Quantity
      </Label>
      <Input
        id="quantity"
        type="number"
        value={stock.quantity}
        onChange={(e) =>
          setStock({
            ...stock,
            quantity: parseInt(e.target.value) || 0,
          })
        }
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="supplier" className="text-right">
        Supplier
      </Label>
      <Select
        value={stock.supplier?.toString() || "none"}
        onValueChange={(val) => setStock({ ...stock, supplier: val === "none" ? "" : parseInt(val) })}
      >
        <SelectTrigger className="col-span-3">
          <SelectValue placeholder="Select supplier..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None</SelectItem>
          {suppliersList.map((s) => (
            <SelectItem key={s.id} value={s.id.toString()}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="cost" className="text-right">
        Unit Cost
      </Label>
      <Input
        id="cost"
        type="number"
        value={stock.cost || 0}
        onChange={(e) =>
          setStock({
            ...stock,
            cost: parseFloat(e.target.value) || 0,
          })
        }
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="location" className="text-right">
        Location
      </Label>
      <Input
        id="location"
        value={stock.location}
        onChange={(e) => setStock({ ...stock, location: e.target.value })}
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="minimumStockLevel" className="text-right">
        Minimum Stock Level
      </Label>
      <Input
        id="minimumStockLevel"
        type="number"
        value={stock.minimumStockLevel}
        onChange={(e) =>
          setStock({
            ...stock,
            minimumStockLevel: parseInt(e.target.value) || 0,
          })
        }
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="maximumStockLevel" className="text-right">
        Maximum Stock Level
      </Label>
      <Input
        id="maximumStockLevel"
        type="number"
        value={stock.maximumStockLevel}
        onChange={(e) =>
          setStock({
            ...stock,
            maximumStockLevel: parseInt(e.target.value) || 0,
          })
        }
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="batchNumber" className="text-right">
        Batch Number
      </Label>
      <Input
        id="batchNumber"
        value={stock.batchNumber}
        onChange={(e) => setStock({ ...stock, batchNumber: e.target.value })}
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="expiryDate" className="text-right">
        Expiry Date
      </Label>
      <Input
        id="expiryDate"
        type="date"
        value={stock.expiryDate}
        onChange={(e) => setStock({ ...stock, expiryDate: e.target.value })}
        className="col-span-3"
      />
    </div>
    <div className="grid grid-cols-4 items-center gap-4">
      <Label htmlFor="notes" className="text-right">
        Notes
      </Label>
      <Textarea
        id="notes"
        value={stock.notes}
        onChange={(e) => setStock({ ...stock, notes: e.target.value })}
        className="col-span-3"
      />
    </div>
  </div>
);

const StocksPage = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const canManageStock = usePermission('Stock');

  // State for Server-Side Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const { stocks, pagination, isLoading: stocksLoading } = useSelector(
    (state) => state.stock
  );

  // NOTE: 'stocks' from slice is slightly different depending on if backend returns {data, pagination} or just [].
  // Logic in slice handles this but we need to ensure we use what is in state.

  const { products, isLoading: productsLoading } = useSelector(
    (state) => state.product
  );

  const { suppliers } = useSelector((state) => state.supplier);
  // suppliers contains { success, data: [...] } from API, extract the array
  const suppliersList = suppliers?.data || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("DESC");

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to page 1 on search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const [showAddStockDialog, setShowAddStockDialog] = useState(false);
  const [showEditStockDialog, setShowEditStockDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [newStock, setNewStock] = useState({
    product: "",
    quantity: 0,
    supplier: "",
    cost: 0,
    location: "",
    minimumStockLevel: 0,
    maximumStockLevel: 0,
    batchNumber: "",
    expiryDate: "",
    notes: "",
  });

  // Stock template state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Fetch stocks when dependencies change
  useEffect(() => {
    dispatch(getStocks({
      page,
      limit,
      search: debouncedSearch,
      sortColumn,
      sortDirection
    }));
  }, [page, limit, debouncedSearch, sortColumn, sortDirection, dispatch]);

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getSuppliers());
  }, []);

  const summaryCards = [
    {
      title: "Total Stock Items",
      value: pagination?.total || stocks.length,
      percentage: 100,
      iconBg: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
      iconColor: "text-blue-600",
      valueColor: "text-slate-900 dark:text-slate-100",
    },
    {
      title: "Low Stock Items",
      value: stocks.filter((s) => s.quantity <= (s.data?.minimumStockLevel || 5)).length,
      percentage: stocks.length
        ? (stocks.filter((s) => s.quantity <= (s.data?.minimumStockLevel || 5)).length /
          stocks.length) *
        100
        : 0,
      iconBg: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
      iconColor: "text-orange-500",
      valueColor: "text-orange-500",
      borderColor: "border-orange-100 dark:border-orange-800",
    },
    {
      title: "Out of Stock Items",
      value: stocks.filter((s) => s.quantity === 0).length,
      percentage: stocks.length
        ? (stocks.filter((s) => s.quantity === 0).length / stocks.length) * 100
        : 0,
      iconBg: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
      iconColor: "text-red-500",
      valueColor: "text-red-500",
      borderColor: "border-red-100 dark:border-red-800",
    },
    {
      title: "In Stock",
      value: stocks.filter((s) => s.quantity > 0).length,
      percentage: stocks.length
        ? (stocks.filter((s) => s.quantity > 0).length / stocks.length) * 100
        : 0,
      iconBg: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
      iconColor: "text-green-600",
      valueColor: "text-green-600",
    },
  ];

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      setSortColumn(column);
      setSortDirection("ASC");
    }
  };

  // No client-side sorting/filtering needed anymore
  const filteredStocks = stocks;

  // ... rest of the code ...

  const handleAddStock = () => {
    dispatch(addStock(newStock));
    setShowAddStockDialog(false);
    setNewStock({
      product: "",
      quantity: 0,
      supplier: "",
      cost: 0,
      location: "",
      minimumStockLevel: 0,
      maximumStockLevel: 0,
      batchNumber: "",
      expiryDate: "",
      notes: "",
    });
  };

  const handleUpdateStock = () => {
    if (!selectedStock?.id) return;
    dispatch(updateStock({ id: selectedStock.id, ...selectedStock }));
    setShowEditStockDialog(false);
  };

  const handleDeleteStock = () => {
    if (!selectedStock?.id) return;
    dispatch(deleteStock(selectedStock.id));
    setShowDeleteConfirmDialog(false);
  };

  // Download stock template
  const handleDownloadStockTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API_URL}stocks/template/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, 'stock_update_template.xlsx');
      toast({
        title: "✓ Template downloaded",
        description: "Update quantities and upload to bulk update stock",
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error.response?.data?.message || error.message,
        variant: "destructive",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle file selection for import
  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
    }
  };

  // Handle bulk stock update
  const handleBulkStockUpdate = async () => {
    if (!importFile) {
      toast({ title: "No file selected", description: "Please select an Excel file to import", variant: "destructive" });
      return;
    }

    setImportLoading(true);
    try {
      // Read the Excel file
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast({ title: "Empty file", description: "The file contains no data to import", variant: "destructive" });
        setImportLoading(false);
        return;
      }

      // Send to backend
      const response = await axios.post(`${API_URL}stocks/bulk-update`, {
        stockUpdates: jsonData
      }, { withCredentials: true });

      setImportResults(response.data.data);
      toast({
        title: "✓ Stock update complete",
        description: response.data.message,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });

      // Refresh stocks list
      dispatch(getStocks({ page, limit, search: debouncedSearch, sortColumn, sortDirection }));
    } catch (error) {
      toast({
        title: "Import failed",
        description: error.response?.data?.message || error.message,
        variant: "destructive",
      });
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="mx-auto p-4 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Stock Management</h1>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleDownloadStockTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <DownloadSimple className="mr-2 h-4 w-4" />
            )}
            Template
          </Button>
          {canManageStock && (
            <>
              <Button
                variant="outline"
                onClick={() => { setShowImportDialog(true); setImportFile(null); setImportResults(null); }}
              >
                <UploadSimple className="mr-2 h-4 w-4" />
                Import
              </Button>
              <Button
                onClick={() => setShowAddStockDialog(true)}
                className="bg-primary hover:bg-primary-dark"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Stock
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards - Compact Enterprise Style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {summaryCards.map((card, index) => (
          <div key={index} className={`flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border shadow-sm ${card.borderColor || ''}`}>
            <div className={`p-2 ${card.iconBg}`}>
              <Package className={`h-5 w-5 ${card.iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{card.title}</p>
              <div className="flex items-center gap-2">
                <p className={`text-lg font-bold ${card.valueColor}`}>{card.value}</p>
                <span className="text-[10px] text-slate-400">({card.percentage.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4">
        {/* <h2 className="text-xl font-semibold mb-2">Stock List</h2> */}
        <div className="flex justify-between">
          <div className="relative">
            <Input
              className="pl-8"
              placeholder="Search stocks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <MagnifyingGlass className="absolute left-2 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <Button variant="outline" className="ml-2">
            <FunnelSimple className="mr-2 h-4 w-4" /> Filter
          </Button>
        </div>
      </div>

      <div className="enterprise-table-container">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <input
                  type="checkbox"
                  className="rounded bg-gray-200 border-gray-300 dark:border-slate-600"
                />
              </TableHead>
              <TableHead
                onClick={() => handleSort("product")}
                className="cursor-pointer"
              >
                Product{" "}
                {sortColumn === "product" &&
                  (sortDirection === "asc" ? (
                    <ArrowUp className="inline" />
                  ) : (
                    <ArrowDown className="inline" />
                  ))}
              </TableHead>
              <TableHead
                onClick={() => handleSort("quantity")}
                className="cursor-pointer"
              >
                Quantity{" "}
                {sortColumn === "quantity" &&
                  (sortDirection === "asc" ? (
                    <ArrowUp className="inline" />
                  ) : (
                    <ArrowDown className="inline" />
                  ))}
              </TableHead>
              {/* <TableHead
                onClick={() => handleSort("price")}
                className="cursor-pointer"
              >
                Price{" "}
                {sortColumn === "price" &&
                  (sortDirection === "asc" ? (
                    <ArrowUp className="inline" />
                  ) : (
                    <ArrowDown className="inline" />
                  ))}
              </TableHead> */}
              <TableHead
                onClick={() => handleSort("location")}
                className="cursor-pointer"
              >
                Location{" "}
                {sortColumn === "location" &&
                  (sortDirection === "asc" ? (
                    <ArrowUp className="inline" />
                  ) : (
                    <ArrowDown className="inline" />
                  ))}
              </TableHead>
              <TableHead
                onClick={() => handleSort("expiryDate")}
                className="cursor-pointer"
              >
                Expiry Date{" "}
                {sortColumn === "expiryDate" &&
                  (sortDirection === "asc" ? (
                    <ArrowUp className="inline" />
                  ) : (
                    <ArrowDown className="inline" />
                  ))}
              </TableHead>
              <TableHead>Stock Level</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStocks.map((stock) => (
              <TableRow key={stock.id}>
                <TableCell>
                  <input
                    type="checkbox"
                    className="rounded bg-gray-200 border-gray-300 dark:border-slate-600"
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {stock.Product?.title || 'Unknown Product'}
                </TableCell>
                <TableCell>{stock.quantity}</TableCell>
                {/* <TableCell>${stock.price.toFixed(2)}</TableCell> */}
                <TableCell>{stock.data?.location || '-'}</TableCell>
                <TableCell>
                  {stock.data?.expiryDate ? new Date(stock.data.expiryDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <Badge
                    className={
                      stock.quantity <= (stock.data?.minimumStockLevel || 5)
                        ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                        : stock.quantity >= (stock.data?.maximumStockLevel || 100)
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                          : "bg-yellow-500"
                    }
                  >
                    {stock.quantity <= (stock.data?.minimumStockLevel || 5)
                      ? "Low"
                      : stock.quantity >= (stock.data?.maximumStockLevel || 100)
                        ? "High"
                        : "Normal"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="View History"
                    onClick={() => {
                      setSelectedStock(stock);
                      setShowHistoryDialog(true);
                    }}
                  >
                    <ClockCounterClockwise className="h-4 w-4" />
                  </Button>
                  {canManageStock && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedStock(stock);
                          setShowEditStockDialog(true);
                        }}
                      >
                        <PencilSimple className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedStock(stock);
                          setShowDeleteConfirmDialog(true);
                        }}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Rows per page</span>
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              setLimit(parseInt(value));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[70px]">
              <SelectValue placeholder={limit} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {pagination && pagination.pages > 1 && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || stocksLoading}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm font-medium">
              Page {pagination.page} of {pagination.pages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages || stocksLoading}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <Dialog open={showAddStockDialog} onOpenChange={setShowAddStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Stock</DialogTitle>
            <DialogDescription>
              Enter the details of the new stock you want to add to your
              inventory.
            </DialogDescription>
          </DialogHeader>
          {productsLoading ? (
            <div>Loading products...</div>
          ) : (
            <StockForm stock={newStock} setStock={setNewStock} products={products} suppliersList={suppliersList} />
          )}
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleAddStock}
              disabled={productsLoading}
            >
              Add Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditStockDialog} onOpenChange={setShowEditStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stock</DialogTitle>
            <DialogDescription>
              Update the details of the selected stock.
            </DialogDescription>
          </DialogHeader>
          {productsLoading ? (
            <div>Loading products...</div>
          ) : (
            <StockForm
              stock={selectedStock}
              setStock={setSelectedStock}
              isEdit={true}
              products={products}
              suppliersList={suppliersList}
            />
          )}
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleUpdateStock}
              disabled={productsLoading}
            >
              Update Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StockHistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        stock={selectedStock}
      />

      <AlertDialog
        open={showDeleteConfirmDialog}
        onOpenChange={setShowDeleteConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              stock item from your inventory.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteStock}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stock Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Stock Updates</DialogTitle>
            <DialogDescription>
              Upload an Excel file to bulk update stock quantities. Download the template first for the correct format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Download Template Button */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                Need the template? Download it first - it includes all your current products with their stock levels.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadStockTemplate}
                disabled={downloadingTemplate}
              >
                {downloadingTemplate ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <DownloadSimple className="mr-2 h-4 w-4" />
                )}
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="stock-import-file">Select Excel File</Label>
              <Input
                id="stock-import-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
                className="cursor-pointer"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                <h4 className="font-semibold text-sm">Import Results</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Updated: {importResults.success?.length || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-600">✕</span>
                    <span>Errors: {importResults.errors?.length || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">-</span>
                    <span>Skipped: {importResults.skipped?.length || 0}</span>
                  </div>
                </div>
                {importResults.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600 max-h-24 overflow-y-auto">
                    {importResults.errors.map((err, i) => (
                      <div key={i}>Row {err.row}: {err.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              {importResults ? 'Close' : 'Cancel'}
            </Button>
            {!importResults && (
              <Button
                onClick={handleBulkStockUpdate}
                disabled={importLoading || !importFile}
              >
                {importLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <UploadSimple className="mr-2 h-4 w-4" />
                    Update Stock
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StocksPage;
